/**
 * Schémas Zod — catalogue (articles, catégories, référentiels, blocs de texte).
 * Référence : document 06 § 6.4 ; document 09 — E09 et E10.
 */
import { z } from 'zod';
import { ITEM_DOCUMENT_KINDS, ITEM_KINDS, TEXT_BLOCK_CATEGORIES, TEXT_BLOCK_PLACEMENTS, } from '../enums/catalog.enum.js';
import { MAX_PAYMENT_TERM_DAYS } from '../constants/payment.constants.js';
import { UNIT_DECIMALS_MAX, UNIT_DECIMALS_MIN } from '../constants/units.constants.js';
import { paginationQuerySchema, uuidSchema } from './common.schema.js';
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
/** Montant ou prix : nombre ou chaîne décimale, jusqu'à 4 décimales. */
const amountSchema = z
    .union([z.number(), z.string().regex(/^\d{1,11}(\.\d{1,4})?$/, { message: 'Montant invalide.' })])
    .nullable()
    .optional();
/** Taux de TVA exprimé en pourcentage (deux décimales au plus). */
const vatRateValueSchema = z
    .union([
    z.number(),
    z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, { message: 'Taux de TVA invalide.' }),
])
    .refine((value) => Number(value) >= 0 && Number(value) <= 100, {
    message: 'Le taux doit être compris entre 0 et 100.',
});
const codeSchema = (max) => z.string().trim().min(1).max(max);
const labelSchema = (max) => z.string().trim().min(1).max(max);
// --- Articles ----------------------------------------------------------------------------------
/** Paramètres de `GET /items`. */
export const itemListQuerySchema = paginationQuerySchema.extend({
    filter: z
        .object({
        kind: z.enum(ITEM_KINDS).optional(),
        categoryId: uuidSchema.optional(),
        vatRateId: uuidSchema.optional(),
        unitId: uuidSchema.optional(),
        isActive: z.enum(['true', 'false']).optional(),
        search: z.string().trim().max(180).optional(),
    })
        .optional(),
});
const itemBaseSchema = {
    kind: z.enum(ITEM_KINDS),
    // Facultative : l'API génère une référence à partir du nom de la société lorsqu'elle est absente.
    reference: z.string().trim().max(40).optional(),
    manufacturerReference: optionalText(60),
    /** Titre de l'article (ex-« désignation courte ») : obligatoire. */
    shortLabel: labelSchema(120),
    description: optionalText(5000),
    categoryId: uuidSchema.nullable().optional(),
    unitId: uuidSchema.nullable().optional(),
    vatRateId: uuidSchema.nullable().optional(),
    /** Prix catalogue : prix brut appliqué par la société, avant remise éventuelle. */
    cataloguePriceHt: amountSchema,
    /** Prix public : prix conseillé par le fabricant. */
    publicPriceHt: amountSchema,
    costPrice: amountSchema,
    isSellable: z.boolean().optional(),
    isPurchasable: z.boolean().optional(),
    isActive: z.boolean().optional(),
    textBlockId: uuidSchema.nullable().optional(),
};
/** Corps de `POST /items`. */
export const itemCreateSchema = z.object(itemBaseSchema);
/** Corps de `PATCH /items/:id`. */
export const itemUpdateSchema = z
    .object(itemBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
/** Paramètres de `GET /items/:itemId/documents`. */
export const itemDocumentListQuerySchema = paginationQuerySchema.extend({
    filter: z
        .object({
        kind: z.enum(ITEM_DOCUMENT_KINDS).optional(),
        itemSupplierId: uuidSchema.optional(),
    })
        .optional(),
});
/** Paramètres d'URL `:itemId` pour les fichiers d'article. */
export const itemDocumentItemIdParamSchema = z.object({ itemId: uuidSchema });
/** Paramètres d'URL `:itemId` et `:documentId`. */
export const itemDocumentIdParamSchema = z.object({
    itemId: uuidSchema,
    documentId: uuidSchema,
});
/** Corps de `PATCH /items/:itemId/documents/:documentId` : sélection pour l'impression. */
export const itemDocumentUpdateSchema = z.object({
    isPrintSelected: z.boolean(),
});
// --- Catégories --------------------------------------------------------------------------------
/** Paramètres de `GET /item-categories`. */
export const itemCategoryListQuerySchema = paginationQuerySchema.extend({
    filter: z
        .object({
        parentId: uuidSchema.optional(),
        isActive: z.enum(['true', 'false']).optional(),
        search: z.string().trim().max(120).optional(),
    })
        .optional(),
});
const itemCategoryBaseSchema = {
    name: labelSchema(120),
    // Facultatif : le code est dérivé du nom par l'API lorsqu'il n'est pas fourni.
    code: codeSchema(40).optional(),
    description: optionalText(255),
    parentId: uuidSchema.nullable().optional(),
    position: z.number().int().min(0).max(32767).optional(),
    isActive: z.boolean().optional(),
};
/** Corps de `POST /item-categories`. */
export const itemCategoryCreateSchema = z.object(itemCategoryBaseSchema);
/** Corps de `PATCH /item-categories/:id`. */
export const itemCategoryUpdateSchema = z
    .object(itemCategoryBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
// --- Unités ------------------------------------------------------------------------------------
/** Paramètres de `GET /units`. */
export const unitListQuerySchema = paginationQuerySchema;
const unitBaseSchema = {
    code: codeSchema(16),
    label: labelSchema(40),
    decimals: z.number().int().min(UNIT_DECIMALS_MIN).max(UNIT_DECIMALS_MAX).optional(),
};
/** Corps de `POST /units`. */
export const unitCreateSchema = z.object(unitBaseSchema);
/** Corps de `PATCH /units/:id`. */
export const unitUpdateSchema = z
    .object(unitBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
// --- Taux de TVA -------------------------------------------------------------------------------
/** Paramètres de `GET /vat-rates`. */
export const vatRateListQuerySchema = paginationQuerySchema.extend({
    filter: z
        .object({
        isUsed: z.enum(['true', 'false']).optional(),
    })
        .optional(),
});
const vatRateBaseSchema = {
    code: codeSchema(16),
    label: labelSchema(60),
    rate: vatRateValueSchema.optional(),
    isDefault: z.boolean().optional(),
    isUsed: z.boolean().optional(),
    isExempt: z.boolean().optional(),
    legalNote: optionalText(255),
    accountingCode: optionalText(40),
};
/** Corps de `POST /vat-rates`. */
export const vatRateCreateSchema = z.object(vatRateBaseSchema);
/** Corps de `PATCH /vat-rates/:id`. */
export const vatRateUpdateSchema = z
    .object(vatRateBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
// --- Modes de règlement ------------------------------------------------------------------------
/** Paramètres de `GET /payment-methods`. */
export const paymentMethodListQuerySchema = paginationQuerySchema;
const paymentMethodBaseSchema = {
    code: codeSchema(16),
    label: labelSchema(60),
    requiresReference: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
};
/** Corps de `POST /payment-methods`. */
export const paymentMethodCreateSchema = z.object(paymentMethodBaseSchema);
/** Corps de `PATCH /payment-methods/:id`. */
export const paymentMethodUpdateSchema = z
    .object(paymentMethodBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
// --- Conditions de règlement -------------------------------------------------------------------
/** Paramètres de `GET /payment-terms`. */
export const paymentTermListQuerySchema = paginationQuerySchema;
const paymentTermBaseSchema = {
    code: codeSchema(16),
    label: labelSchema(60),
    days: z.number().int().min(0).max(MAX_PAYMENT_TERM_DAYS).optional(),
    endOfMonth: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    legalText: optionalText(255),
};
/** Corps de `POST /payment-terms`. */
export const paymentTermCreateSchema = z.object(paymentTermBaseSchema);
/** Corps de `PATCH /payment-terms/:id`. */
export const paymentTermUpdateSchema = z
    .object(paymentTermBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
// --- Blocs de texte ----------------------------------------------------------------------------
/** Paramètres de `GET /text-blocks`. */
export const textBlockListQuerySchema = paginationQuerySchema.extend({
    filter: z
        .object({
        category: z.enum(TEXT_BLOCK_CATEGORIES).optional(),
        placement: z.enum(TEXT_BLOCK_PLACEMENTS).optional(),
        isActive: z.enum(['true', 'false']).optional(),
        search: z.string().trim().max(180).optional(),
    })
        .optional(),
});
const textBlockBaseSchema = {
    code: codeSchema(40),
    name: labelSchema(120),
    category: z.enum(TEXT_BLOCK_CATEGORIES),
    body: z.string().min(1).max(20000),
    placement: z.enum(TEXT_BLOCK_PLACEMENTS),
    isActive: z.boolean().optional(),
    isDefaultQuote: z.boolean().optional(),
    isDefaultInvoice: z.boolean().optional(),
};
/** Corps de `POST /text-blocks`. */
export const textBlockCreateSchema = z.object(textBlockBaseSchema);
/** Corps de `PATCH /text-blocks/:id`. */
export const textBlockUpdateSchema = z
    .object(textBlockBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
//# sourceMappingURL=catalog.schema.js.map